import { useEffect, useMemo, useState } from "react";
import { TrendingUp, TrendingDown, Flame, Snowflake, Loader2, RefreshCw, Download } from "lucide-react";
import api from "@/lib/api";

const PERIODS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["all", "All time"]];
const FILTERS = [
  ["all", "All players"], ["profit", "In profit"], ["loss", "In loss"],
  ["win_streak", "On win streak"], ["loss_streak", "On loss streak"],
];
const SORTS = [["net", "Net P/L"], ["wagered", "Wagered"], ["games", "Games"], ["streak", "Streak"], ["last", "Last played"]];

const money = (n) => `${n > 0 ? "+" : ""}${Math.round(n).toLocaleString()}`;
const CSV_COLS = [
  ["Username", "username"], ["Name", "display_name"], ["Mobile", "mobile"], ["Games", "games"], ["Wins", "wins"],
  ["Losses", "losses"], ["Win %", "win_rate"], ["Wagered", "wagered"], ["Won", "won_amount"], ["Net P/L", "net"],
  ["Current streak", "current_streak"], ["Best win streak", "max_win_streak"], ["Worst loss streak", "max_loss_streak"],
  ["Balance", "balance"], ["Last played", "last_played"],
];

function exportCsv(rows, period) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [CSV_COLS.map(([h]) => esc(h)).join(","), ...rows.map((r) => CSV_COLS.map(([, k]) => esc(k === "mobile" ? `'${r[k] ?? ""}` : r[k])).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `timeup-players-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "-");

export function AdminPlayers({ onView }) {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("net");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/player-analytics", { params: { period } }).then(({ data }) => setData(data)),
      api.get("/admin/game-feed", { params: { limit: 100 } }).then(({ data }) => setFeed(data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let r = data?.players || [];
    if (filter === "profit") r = r.filter((p) => p.net > 0);
    if (filter === "loss") r = r.filter((p) => p.net < 0);
    if (filter === "win_streak") r = r.filter((p) => p.current_streak >= 2);
    if (filter === "loss_streak") r = r.filter((p) => p.current_streak <= -2);
    if (q) r = r.filter((p) => `${p.username} ${p.mobile} ${p.display_name}`.toLowerCase().includes(q.toLowerCase()));
    const key = { net: (p) => p.net, wagered: (p) => p.wagered, games: (p) => p.games, streak: (p) => Math.abs(p.current_streak), last: (p) => p.last_played || "" }[sort];
    return [...r].sort((a, b) => (key(b) > key(a) ? 1 : key(b) < key(a) ? -1 : 0));
  }, [data, filter, sort, q]);

  const s = data?.summary;
  return (
    <div className="space-y-6" data-testid="admin-players">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => setPeriod(v)} data-testid={`players-period-${v}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${period === v ? "bg-pazer-red text-white" : "bg-white/5 hover:bg-white/10 text-white/70"}`}>{l}</button>
        ))}
        <button onClick={() => exportCsv(rows, period)} disabled={!rows.length} data-testid="players-export"
          className="ml-auto px-4 py-2 rounded-full bg-green-500/15 hover:bg-green-500/25 text-green-400 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-40">
          <Download className="h-4 w-4" /> Export Excel (CSV)
        </button>
        <button onClick={load} data-testid="players-refresh" className=" h-9 w-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="players-summary">
          <Card label="Total wagered" value={Math.round(s.wagered).toLocaleString()} testid="sum-wagered" />
          <Card label="Paid to winners" value={Math.round(s.paid_out).toLocaleString()} testid="sum-paid" />
          <Card label="House profit" value={money(s.house_profit)} tone={s.house_profit >= 0 ? "text-green-400" : "text-pazer-red"} testid="sum-profit" />
          <Card label="Games played" value={s.games.toLocaleString()} testid="sum-games" />
          <Card label="Players in profit" value={s.players_in_profit} tone="text-green-400" icon={TrendingUp} testid="sum-in-profit" />
          <Card label="Players in loss" value={s.players_in_loss} tone="text-pazer-red" icon={TrendingDown} testid="sum-in-loss" />
          <Card label="On win streak (2+)" value={s.on_win_streak} tone="text-orange-400" icon={Flame} testid="sum-win-streak" />
          <Card label="On loss streak (2+)" value={s.on_loss_streak} tone="text-sky-400" icon={Snowflake} testid="sum-loss-streak" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        {FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`players-filter-${v}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === v ? "border-pazer-red bg-pazer-red/15 text-white" : "border-white/10 text-white/60 hover:bg-white/5"}`}>{l}</button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player" data-testid="players-search"
          className="ml-auto bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-pazer-red" />
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="players-sort"
          className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none">
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
      </div>

      <div className="rounded-2xl pz-glass overflow-x-auto">
        <table className="w-full text-sm" data-testid="players-table">
          <thead className="text-white/40 text-xs uppercase">
            <tr className="border-b border-white/10">
              {["Player", "Games", "W / L", "Win %", "Wagered", "Won", "Net P/L", "Current streak", "Best W / Worst L", "Balance", "Last played"].map((h) => (
                <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.user_id} onClick={() => onView(p.user_id)} data-testid={`player-row-${p.user_id}`}
                className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                <td className="px-3 py-2.5"><div className="font-semibold">{p.username}</div><div className="text-[11px] text-white/40">{p.mobile}</div></td>
                <td className="px-3 font-mono-pz">{p.games}</td>
                <td className="px-3 font-mono-pz"><span className="text-green-400">{p.wins}</span> / <span className="text-pazer-red">{p.losses}</span></td>
                <td className="px-3 font-mono-pz">{p.win_rate}%</td>
                <td className="px-3 font-mono-pz">{Math.round(p.wagered).toLocaleString()}</td>
                <td className="px-3 font-mono-pz">{Math.round(p.won_amount).toLocaleString()}</td>
                <td className={`px-3 font-mono-pz font-bold ${p.net > 0 ? "text-green-400" : p.net < 0 ? "text-pazer-red" : ""}`} data-testid={`player-net-${p.user_id}`}>{money(p.net)}</td>
                <td className="px-3"><Streak n={p.current_streak} /></td>
                <td className="px-3 font-mono-pz"><span className="text-orange-400">{p.max_win_streak}W</span> / <span className="text-sky-400">{p.max_loss_streak}L</span></td>
                <td className="px-3 font-mono-pz">{Math.round(p.balance).toLocaleString()}</td>
                <td className="px-3 text-xs text-white/50 whitespace-nowrap">{when(p.last_played)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="text-center text-white/40 py-10">No players match.</td></tr>}
          </tbody>
        </table>
      </div>

      <GameFeed feed={feed} onView={onView} />
    </div>
  );
}

function Streak({ n }) {
  if (!n) return <span className="text-white/30">-</span>;
  const win = n > 0;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${win ? "bg-orange-400/15 text-orange-400" : "bg-sky-400/15 text-sky-400"}`}>
      {win ? <Flame className="h-3 w-3" /> : <Snowflake className="h-3 w-3" />}{Math.abs(n)} {win ? "wins" : "losses"} in a row
    </span>
  );
}

function Card({ label, value, tone = "", icon: I, testid }) {
  return (
    <div className="rounded-2xl pz-glass p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-white/40 flex items-center gap-1.5">{I && <I className="h-3.5 w-3.5" />}{label}</div>
      <div className={`font-mono-pz font-bold text-2xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
}

function GameFeed({ feed, onView }) {
  return (
    <div className="rounded-2xl pz-glass p-5" data-testid="game-feed">
      <h3 className="font-display font-bold text-lg mb-3">Latest games</h3>
      <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
        {feed.map((g, i) => (
          <div key={`${g.game_id}-${i}`} onClick={() => onView(g.user_id)} data-testid={`feed-row-${i}`}
            className="flex items-center gap-3 rounded-lg bg-black/30 hover:bg-black/50 px-3 py-2 text-sm cursor-pointer transition-colors">
            <span className={`h-2 w-2 rounded-full ${g.result === "won" ? "bg-green-400" : "bg-pazer-red"}`} />
            <span className="font-semibold w-32 truncate">{g.username}</span>
            <span className="text-white/40 text-xs w-24">{g.mode === "room" ? `Room ${g.room_code || ""}` : "Fixed"}</span>
            <span className="text-white/60 text-xs font-mono-pz w-36">target {g.target}s · stop {g.recorded ?? "-"}s</span>
            <span className="text-white/50 text-xs font-mono-pz">bet {g.entry_fee}</span>
            <span className={`ml-auto font-mono-pz font-bold ${g.net > 0 ? "text-green-400" : "text-pazer-red"}`}>{money(g.net)}</span>
            <span className="text-[11px] text-white/40 w-28 text-right">{when(g.created_at)}</span>
          </div>
        ))}
        {feed.length === 0 && <p className="text-white/40 text-sm">No games yet.</p>}
      </div>
    </div>
  );
}
